import { useFilters } from './filters';
import { RU_FEED, TRANSFERS } from '../lib/feedContext';
import { transferEligible, transferFloor } from '../lib/transferFeed';
/**
 * Строка ленты.
 *
 * Собрана вычитанием. Колонок ROI и «рынок» здесь нет намеренно: процент
 * поднимает наверх то, что брать нельзя, а «рынок» семь строк подряд повторяет
 * одно и то же и несёт ноль информации. Строки не равны: высота и кегль суммы —
 * это и есть сортировка, которую видно, не читая ни одной цифры.
 *
 * На ноге стоит крупно ровно то, что вбивается в чужое поле: центы и штуки на
 * бирже, рубли у ру-конторы, доллары у зарубежной. Прибыль строки — режимом
 * из сита (`$` / `₽` / `%`). Очерёдность подписана цифрой на самой ноге, а не
 * в шапке колонки: шапку приходилось объяснять, а на узком экране она уезжает.
 *
 * Атрибут `data-fork-row` обязателен. По нему гейт выката отличает
 * отрисованную ленту от пустого `#root`, и без него деплой откатится, показав
 * «лента не отрисована» при исправной странице.
 */

import { memo } from 'react';

import { InitiatorDot } from './InitiatorDot';

import { marketLabel } from '../lib/protocol';
import { leagueTitle } from '../lib/sports';
import type { RowLeg } from '../lib/row';
import { useSettings } from '../store/settings';
import { remember } from '../lib/sight';
import { hrefOf, matchQuery, openBook, searchDoor } from './act';
import { useSession } from '../access/session';
import { Bell } from './Bell';
import { Coupon } from './Coupon';
import { useDeskCall } from './deskCall';
import { rub } from './format';
import { copy, useCopyFlash } from './Grab';
import type { Meta } from './meta';
import { venueName } from './meta';
import { legOrder, legsFirstOf } from './order';
import { matchTitle } from './matchTitle';
import { betOn } from './pick';
import { useProfile } from './profile';
import type { FeedRow } from './rows';
import { moneyAmount, profitText, useShow } from './show';
import { Sign } from './Sign';
import { lifePhrase, stillText } from './still';
import { logoOf } from './venues';

/**
 * Метки строки: чем эта вилка хуже соседней.
 *
 * До 08.08 в ленте жили две — «цена под сомнением» и «может не дожить», — и обе
 * означали одно и то же красным. Всё остальное, по чему принимают решение,
 * лежало в раскрытой карточке: залипшая цена, отсутствие ссылки. Замер живого
 * кадра 08.08: из двадцати верхних строк у восьми застоявшаяся нога, у
 * пятнадцати нет ссылки хотя бы на одну ногу — и ни одна строка об этом не
 * говорила. Владелец: «ощущение, что любую можно брать».
 *
 * Три веса вместо одного красного, потому что доводы разной силы, а красный на
 * каждой второй строке перестаёт быть стоп-сигналом:
 *
 *   `stop` — цены, скорее всего, уже нет. Гейт назвал цену неверной
 *            (палп, аномалия, нет согласия рынка);
 *   `warn` — вилка живая, но не под ваш темп. В «работе» не красим: такие
 *            строки либо спрятаны по возрасту, либо стоят ниже из-за штрафа `sv`;
 *   `note` — не риск, а работа: входа нет, матч искать руками. В музее сюда
 *            же уходит серое «цена стоит N мин» — не оранжевая смерть.
 *
 * Порядок в массиве — порядок чтения: сначала худшее.
 */
export interface Mark {
  weight: 'stop' | 'warn' | 'note';
  text: string;
  /** Развёрнутое пояснение — в подсказке: в строке на него места нет. */
  why: string;
}

export function marksOf(row: FeedRow, museum = false, transferMin = -5): Mark[] {
  const marks: Mark[] = [];
  if (TRANSFERS && !transferEligible(row.row, transferMin)) {
    marks.push({ weight: 'note', text: 'вне подборки', why: 'пара больше не соответствует диапазону минусовых PM–БК; ваши рабочие значения сохранены' });
  }
  if (row.row.doubt === 'dead') {
    marks.push({
      weight: 'stop',
      text: 'перепроверь',
      why: 'один из двух кэфов, скорее всего, неверный. откройте карточку и сверьте на сайте конторы',
    });
  }
  // Покой ноги — другой час, не смерть рынка. В «работе» молчим: строку старше
  // минуты в лайве режет возраст вилки, а не оранжевая краска. Музей называет
  // серым, чтобы рентген не потерял замер.
  if (row.still !== null && museum) {
    marks.push({
      weight: 'note',
      text: `цена стоит ${stillText(row.still)}`,
      why: 'настоящую вилку рынок съедает за секунды. если цена стоит так долго, на неё скорее всего никто не смотрит: вилки уже нет, просто мы об этом ещё не знаем',
    });
  }
  if (row.risky && row.row.doubt !== 'dead' && museum) {
    marks.push({
      weight: 'warn',
      text: 'может не дожить',
      why: 'такие вилки обычно живут меньше, чем у вас уходит между первой и второй ногой',
    });
  }
  // Одна линия Betby под двумя вывесками: строка показана второй раз, чтобы
  // ставить можно было там, где есть счёт. Кэфы в ней — донора, и у второй
  // вывески своя цена.
  if (typeof row.row.fork.dub === 'string' && row.row.fork.dub !== '') {
    marks.push({
      weight: 'note',
      text: 'дубль',
      why: `та же вилка, но нога открывается у другой вывески одной линии (у нас — ${row.row.fork.dub}). кэф показан донорский: перед ставкой сверьте цену на сайте`,
    });
  }
  // Вилка выпала из последнего поиска, но срок ещё не вышел. Цифры последние.
  // Красным не красим: «перепроверь» значит «кэф врёт», а здесь просто нет нового ответа.
  if (row.row.fork.lag === true) {
    marks.push({
      weight: 'note',
      text: 'не обновляли',
      why: 'в последнем поиске этой вилки не было. кэф и процент — последние, какие пришли',
    });
  }
  if (row.linkless) {
    marks.push({
      weight: 'note',
      text: 'входа нет',
      why: 'хотя бы на одну контору ссылки у нас нет. матч и исход придётся искать руками, уже после того как первая нога поставлена',
    });
  }
  return marks;
}

/*
 * Полоса «прожито от медианы живучести» снята 08.08 (владелец). Две причины,
 * каждая сама по себе достаточная.
 *
 * Читать её было нельзя в принципе: шкала у каждой строки своя — 100% у одной
 * вилки это 10 секунд, у другой 300, — и одинаковые на вид полосы означали
 * разное. Медиану, от которой считается доля, человек не видит нигде.
 *
 * И она всё равно была полной всегда: замер живого кадра 08.08 — 191 строка из
 * 191 с известной медианой залита на 100%. Это арифметика выживших: медиана
 * жизни вилки 10 с, медианный возраст строки на экране 50 с. Всё, что человек
 * успел увидеть, уже пережило свою медиану; молодые умирают раньше, чем до них
 * доедет взгляд. Полоса, стоящая на каждой строке одинаково, не сообщает ничего
 * и учит не смотреть на низ строки — вместе с тем, что там окажется потом.
 *
 * Возраст остался словами («живёт 45 с»), риск — ярлыком «может не дожить»:
 * оба честны и не требуют знать невидимую шкалу.
 */

/**
 * Чем ограничен объём — словами и числом.
 *
 * Виды потолка названы по-разному не ради точности формулировки: «стакан» и
 * «лимит» — чужие факты, «банк» — решение оператора, «допущение» — его же
 * догадка. Сложив их в одно слово, мы отправили бы человека поднимать лимит,
 * которого никто не ставил.
 */
export function capNote(row: FeedRow): string | null {
  const by = row.row.cappedBy;
  if (by === null) return null;
  if (by === 'банк') return 'весь банк';
  if (by === 'баланс БК') return 'баланс БК';
  if (by === 'баланс' || by === 'фрибет') {
    const held = row.row.capBalance;
    const word = by === 'фрибет' ? 'фрибет' : 'баланс';
    return held === undefined ? word : `${word} ${rub(held.value)}${held.cur === 'RUB' ? ' ₽' : ' $'}`;
  }
  const leg = row.row.legs.find((l) => l.cap.value !== null && l.cap.kind !== 'book');
  if (by === 'стакан') return 'стакан';
  if (leg?.cap.value != null) {
    return `${by} ${rub(leg.cap.value)}${leg.cur === 'RUB' ? ' ₽' : ' $'}`;
  }
  return by;
}

const title = (row: FeedRow): string => matchTitle(row);

// Порядок ног переехал в `order.ts`: правило «первой та, где выше шанс не
// встать» перестало быть однострочником про биржу, а строка, карточка, витрина
// и клавиатура обязаны брать его из одного места. Реэкспорт оставлен, чтобы не
// править четыре импорта ради переезда.
export { legOrder };

/**
 * Класс боковых полей строки: лайв или прематч.
 *
 * Пустая строка, если линия неизвестна — не угадываем. Чужой `live:` в фиде
 * площадки ещё не значит, что матч идёт; здесь только то, что сказал протокол.
 */
export function lineTone(line: string | undefined): string {
  if (line === 'live') return 'nx-fk--live';
  if (line === 'prematch') return 'nx-fk--prematch';
  return '';
}

function LegView({
  leg,
  index,
  what,
  meta,
  rowId,
  query,
  initiatorBy,
}: {
  leg: RowLeg;
  index: number;
  what: string;
  meta: Meta;
  rowId: string;
  /** Название в поиск, если у площадки нет страницы события. */
  query: string | null;
  initiatorBy?: string;
}): React.ReactElement {
  const share = leg.unit === 'share';
  // Тревога стоит на цене, а не на количестве штук: дорогой шер — это не «много
  // купили», это «шанс, который рынок уже посчитал».
  const over = share && leg.cents !== null && leg.cents > 55;
  const logo = logoOf(leg.source);
  const href = hrefOf(rowId, leg);
  const at = venueName(meta, leg.source);
  const fx = useSettings((s) => s.fx);
  const { took, fail, flash, take } = useCopyFlash();
  const go = (): void => {
    if (href === null) return;
    if (query !== null) void copy(query);
    openBook(rowId, leg);
    remember(rowId, [href]);
  };
  const clip = (): void => {
    if (href === null) return;
    take(href);
  };
  const hint =
    href === null
      ? undefined
      : took
        ? 'ссылка в буфере'
        : fail
          ? 'не скопировалось'
          : `${at} · правый клик — скопировать ссылку`;
  return (
    <span
      className={`nx-fk__leg ${['one', 'two', 'three'][index] ?? 'three'}${share ? ' share' : ''}`}
    >
      {/* Монеток ①② здесь больше нет: на кегле строки они не читались
          (07.08, владелец), а очерёдность — знание момента СТАВКИ, и живёт она
          в раскрытой карточке — цифрой шага в плитке ноги, подписью «лучше после
          первой» и приглушённой второй кнопкой. Строке остаётся отвечать
          «сколько и куда», а не «в каком порядке». */}
      <span className="n">
        <b>
          <span className={over ? 'over' : ''}>
            {share ? leg.headline : moneyAmount(leg, fx, meta)}
          </span>
        </b>
        {share && <s>{leg.detail}</s>}
      </span>
      {/* Имя и знак не кладём в <a href=контора>: 13.08 и 17.08 это съедало
          ряд (:visited, глобальный `a { color }`, блокировщик ссылок на БК).
          Пустой хит поверх тоже прятали — подпись оставалась, клика не было.
          Вкладку открываем сами, только с названия. Знак («точка») и исход
          раскрывают карточку: 01.09 тестер жал в знак и уезжал на биржу.
          Правый клик — тот же адрес в буфер: из РФ вкладка часто не грузится,
          ссылку кладут в профиль с прокси. `auxclick` раньше открывал и
          правой — жест тестера. */}
      <span className="nx-fk__spot">
        <InitiatorDot
          initiator={leg.initiator}
          initiatorCount={leg.initiatorCount}
          by={initiatorBy}
        />
      <span className="nx-fk__book">
        {logo !== null && (
          <img className="nx-fk__mk" src={logo} alt="" loading="lazy" decoding="async" />
        )}
        <span className="nx-fk__who">
          <span
            className={href !== null ? `nx-fk__at nx-fk__at--go${flash}` : 'nx-fk__at'}
            role={href !== null ? 'link' : undefined}
            tabIndex={href !== null ? 0 : undefined}
            data-href={href ?? undefined}
            title={hint}
            onClick={
              href !== null
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    go();
                  }
                : undefined
            }
            onPointerDown={href !== null ? (event) => event.stopPropagation() : undefined}
            onContextMenu={
              href !== null
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    clip();
                  }
                : undefined
            }
            onAuxClick={
              href !== null
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (event.button === 1) go();
                  }
                : undefined
            }
            onKeyDown={
              href !== null
                ? (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    go();
                  }
                : undefined
            }
          >
            {at}
          </span>
          <span className="nx-fk__sep"> · </span>
          <span className="nx-fk__what">{what}</span>
        </span>
      </span>
      </span>
      {/* Кэф / $ — сосед ноги, не ребёнок пятна: на широком столбик тот же
          (сумма → контора → кэф), на узком сетка кладёт кэф справа от суммы,
          а не третьим ярусом под конторой. */}
      <i className={over ? 'over' : ''}>
        {share ? `${(leg.usd ?? 0).toFixed(2)} $${over ? ' · выше 55 ¢' : ''}` : leg.detail}
      </i>
    </span>
  );
}

/**
 * Строка перерисовывается только когда изменилась она сама.
 *
 * Обработчики принимают `id`, а не замыкаются на строке: замыкание, созданное
 * заново на каждый кадр ленты, отменяло бы `memo` — а вместе с ним и смысл
 * всей правки. Наведение обязано трогать две строки (прежнюю и новую), а не
 * все четыреста.
 */
function CallMark({ row }: { row: FeedRow }): React.ReactElement {
  const openFork = useDeskCall((s) => s.openFork);
  return (
    <button
      type="button"
      className="nx-fk__call"
      title="позвать учеников на эту вилку"
      aria-label="call: позвать учеников на эту вилку"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        openFork(row);
      }}
    >
      <Bell />
    </button>
  );
}

export const Row = memo(function Row({
  row,
  meta,
  lead,
  selected,
  held = false,
  onOpen,
  onPop,
  onHover,
  clipped = false,
}: {
  row: FeedRow;
  meta: Meta;
  lead: boolean;
  selected: boolean;
  /** Вилка «в работе»: раскрыта или человек уже нажимал её кнопки. */
  held?: boolean;
  onOpen: (id: string) => void;
  /** Двойной щелчок: та же вилка отдельным окном. */
  onPop: (id: string) => void;
  onHover: (id: string) => void;
  /** Закреплена на полке: левое поле купона нажато. */
  clipped?: boolean;
}): React.ReactElement {
  const fork = row.row.fork;
  const first = legsFirstOf(useProfile((s) => s.profile?.legsFirst));
  const legs = legOrder(row.row.legs, first);
  // Полная фраза: «да» без рынка в строке так же слепа, как в карточке.
  // Общее «Тотал …» в подзаголовке строки дублируется — лучше, чем искать глазами.
  const what = legs.map((leg) => betOn(leg, fork));
  const ghost = row.ghost !== null;
  // Полка калькулятора не кроет событие пеленой: тестер (18.09) просил пометку
  // «вилки нет» рядом с названием, а не серую строку вместо матча.
  const veil = ghost && !clipped && !held;
  const old = row.lifespan !== null && row.age > row.lifespan;
  const museum = useProfile((s) => s.profile?.name === 'ничего не прятать');
  const unit = useShow((s) => s.unit);
  const fx = useSettings((s) => s.fx);
  const transferMin = useFilters((s) => s.roiMin);
  const transferProfileMin = useProfile((s) => s.profile?.minRoi);
  const marks = marksOf(row, museum, Math.max(transferFloor(transferMin), transferFloor(transferProfileMin)));
  const cap = capNote(row);
  const gain = profitText(row.row.profit, row.row.roiPct, fx, unit);
  const tone = lineTone(fork.line);
  const caller = useSession((s) => s.me.caller);
  const classes = [
    'nx-fk',
    `nx-fk--${RU_FEED ? 'take' : row.tier}`,
    caller ? 'nx-fk--caller' : '',
    // Трёхногая вилка (1-Х-2 и подобные) — треть зеркальной ленты, а сетка
    // строки была двухместной: третья нога рисовалась ПОВЕРХ второй, и обе
    // читались кашей из цифр. Модификатор раздвигает сетку на колонку.
    legs.length > 2 ? 'trio' : '',
    lead ? 'lead' : '',
    veil ? 'ghost' : '',
    row.born ? 'born' : '',
    selected && !ghost ? 'sel' : '',
    held ? 'held' : '',
    tone,
  ]
    .filter(Boolean)
    .join(' ');

  // Серую тоже открывают: вилка скачет, серая строка — шанс поймать кэфы
  // (14.10, Сергей). Открытая становится held и не истекает.

  // Строка — `div` с ролью кнопки, а не `button`. Причина одна: внутри неё
  // живёт вторая кнопка (окном), а кнопка внутри кнопки — недопустимая
  // разметка, и браузеры разбирают её каждый по-своему. Роль, tabindex и
  // обработчик клавиш дают ровно то же поведение, включая читалки.
  return (
    <div
      role="button"
      tabIndex={0}
      className={classes}
      data-fork-row=""
      data-id={row.id}
      data-line={tone === '' ? undefined : fork.line}
      onClick={() => onOpen(row.id)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen(row.id);
      }}
      onMouseEnter={() => onHover(row.id)}
    >
      <Coupon kind="pin" id={row.id} on={clipped} />
      {!ghost && <Coupon kind="pop" id={row.id} onPop={onPop} />}
      {caller && !ghost && <CallMark row={row} />}
      <Sign sport={fork.sport} name={fork.sportName} />
      <div className="nx-fk__work">
      <span className="nx-fk__m" title={title(row)}>
        {title(row)}
      </span>
      {/* Пометка живёт ОТДЕЛЬНЫМ ярлыком, а не хвостом текста: строка с длинным
          рынком обрезалась многоточием, и «может не дожить» пропадало первым —
          то есть предупреждение исчезало ровно на тех строках, где текста
          больше всего (07.08, владелец). Теперь обрезается текст, а не ярлык. */}
      <span className="nx-fk__meta">
        {/* Метка идёт ПЕРЕД текстом, а не после него. Стояла после — и её место
            на экране задавалось длиной названия рынка: «тотал 21.5 меньше карта
            1 по очкам» уводил ярлык вдвое правее, чем «победа 2». Глаз искал
            предупреждение заново на каждой строке (12.08, владелец). Теперь
            ярлык прижат к левому краю колонки названия — то есть ровно под
            первой буквой матча, одинаково на двух- и трёхногих строках.

            Больше двух меток строка не несёт: третья съедает подпись целиком, а
            читают всё равно первую. Остальные ждут в карточке. */}
        {marks.slice(0, 2).map((mark) => (
          <em key={mark.text} className={mark.weight} title={mark.why}>
            {mark.text}
          </em>
        ))}
        <span className="t">
          {leagueTitle(fork)} · {marketLabel(fork.market, fork.sport)} ·{' '}
          {lifePhrase(row.age, fork, undefined, row.lifespan)}
          {/* «Дольше обычного» — похвала, и она имеет право звучать только там,
              где возразить нечем. Прежде она стояла рядом с залипшей ценой: то
              есть на одной строке продукт хвалил вилку за долгую жизнь и молчал
              о том, что живёт она долго именно потому, что на неё никто не
              смотрит. Порог залипания в лайве — те же 150 секунд, и число «150»
              означало одновременно «молодец» и «беги» (08.08, владелец). */}
          {marks.length === 0 && row.still === null && old ? ' · дольше обычного' : ''}
        </span>
      </span>

      {legs.map((leg, i) => (
        <LegView
          key={leg.source}
          leg={leg}
          index={i}
          what={what[i] ?? ''}
          meta={meta}
          rowId={row.id}
          query={searchDoor(leg.source, hrefOf(row.id, leg)) ? matchQuery(row) : null}
          initiatorBy={typeof fork.initiatorBy === 'string' ? fork.initiatorBy : undefined}
        />
      ))}
      </div>

      <div className="nx-fk__gain">
      <span className="nx-fk__sum">
        <span
          className={`nx-fk__val${row.beat === 1 ? ' up' : row.beat === -1 ? ' down' : ''}`}
        >
          {gain}
          {cap !== null && <em className="nx-fk__cap">{cap}</em>}
        </span>
      </span>
      </div>
    </div>
  );
});
